/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { values } from "lodash";
import { Table, Form } from "semantic-ui-react";
import * as moment from "moment";

import { getQuestionConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { DateQuestion } from "@teamprevent/common/questionnaires/components/questions/DateQuestion";
import { ContextAwareProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireSection";
import { useTimeHasAlreadyPassedValidator } from "@teamprevent/common/validators/timeHasAlreadyPassed";
import { useTimeIsAfterValidator } from "@teamprevent/common/validators/timeIsAfter";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";

import { getSpirometryLookupTableValue } from "../consultationQuestions/getSpirometryLookupTableValue";
import { ApplicationState } from "../../applicationState";
import * as questionIds from "../consultationQuestions/questionIds";
import { Ethnicities, Genders, ReadingType, ReasonsForAttending } from "../consultationQuestions/spirometryLookupTable";
import { sections } from "../consultation";

import { HealthSurveillanceConsultationKey } from "./HealthSurveillanceConsultation";

const reviewDateOtherAnswer = "Other";

interface AdditionalProps {
    employeeDob: moment.Moment;
}

type AllProps = CustomQuestionsQuestionnaireSectionProps & AdditionalProps;

const Jan1st1900 = moment("1900-01-01");

const HealthSurveillanceConsultationSpirometrySectionUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const { updateAnswer, questionConfigs, sections: propSections, questions: propsQuestions, readonly, employeeDob } = props;

    const questions = values(propsQuestions || {});

    const getAnswerToQuestionInSectionWithId = React.useCallback((questionSectionId: string, questionId: string) => {
        const section = propSections.find(s => s.id === questionSectionId);
        return section?.questions[questionId]?.answer ?? null;
    }, [propSections]);

    const updateQuestionAnswer = React.useCallback((questionId: string, answer: QuestionAnswer) => {
        updateAnswer({
            sectionId: sections.Spirometry.id,
            id: questionId,
            question: {
                id: questionId,
                isValid: true,
                question: (getQuestionConfig(questionConfigs, questionId) as CustomQuestionConfig).questionText,
                answer
            },
            questionnaireKey: HealthSurveillanceConsultationKey
        });
    }, [questionConfigs, updateAnswer]);

    const calculatePercentRatio = (predicted: number, actual: number): string => {
        if (isNaN(predicted) || isNaN(actual)) {
            return "";
        }
        return ((actual / predicted) * 100).toFixed(2);
    };

    const ethnicity = getAnswerToQuestionWithId(questions, questionIds.SpirometryEthnicity) as string;
    const gender = getAnswerToQuestionWithId(questions, questionIds.SpirometryGender) as string;
    const age = getAnswerToQuestionWithId(questions, questionIds.SpirometryAge) as number;
    const height = getAnswerToQuestionInSectionWithId(sections.HealthQuestions.id, questionIds.Height) as number;

    const predictedFvcAnswer = getAnswerToQuestionWithId(questions, questionIds.SpirometryFvcLitresPredicted) as number;
    const resultFvcAnswer = getAnswerToQuestionWithId(questions, questionIds.SpirometryFvcLitres) as number;

    const predictedFev1Answer = getAnswerToQuestionWithId(questions, questionIds.SpirometryFev1LitresPredicted) as number;
    const resultFev1Answer = getAnswerToQuestionWithId(questions, questionIds.SpirometryFev1Litres) as number;

    const predictedFev1FvcRatioAnswer = getAnswerToQuestionWithId(questions, questionIds.SpirometryFev1FvcRatioPredicted) as number;
    const resultFev1FvcRatioAnswer = getAnswerToQuestionWithId(questions, questionIds.SpirometryFev1FvcRatio) as number;

    const calculateValuesForRow = React.useCallback((
        reading: ReadingType,
        predictedQuestionId: string,
        percentPredictedQuestionId: string,
        predictedAnswer: number,
        resultAnswer: number) => {
        const predicted = getSpirometryLookupTableValue(gender, age, height, ethnicity, reading);
        if (predicted !== predictedAnswer) {
            updateQuestionAnswer(predictedQuestionId, predicted);
        }
        if (!!predicted && !isNaN(resultAnswer)) {
            const percentRatio = calculatePercentRatio(predicted, resultAnswer);
            updateQuestionAnswer(percentPredictedQuestionId, percentRatio);
        }
    }, [age, ethnicity, gender, height, updateQuestionAnswer]);

    const calculateFvcValues = React.useCallback(() => {
        calculateValuesForRow(
            "fvc",
            questionIds.SpirometryFvcLitresPredicted,
            questionIds.SpirometryFvcLitresPercentPredicted,
            predictedFvcAnswer,
            resultFvcAnswer);
    }, [calculateValuesForRow, predictedFvcAnswer, resultFvcAnswer]);

    const calculateFev1Values = React.useCallback(() => {
        calculateValuesForRow(
            "fev1",
            questionIds.SpirometryFev1LitresPredicted,
            questionIds.SpirometryFev1LitresPercentPredicted,
            predictedFev1Answer,
            resultFev1Answer);
    }, [calculateValuesForRow, predictedFev1Answer, resultFev1Answer]);

    const calculateFev1FvcRatioValues = React.useCallback(() => {
        calculateValuesForRow(
            "fev1FvcRatio",
            questionIds.SpirometryFev1FvcRatioPredicted,
            questionIds.SpirometryFev1FvcRatioPercentPredicted,
            predictedFev1FvcRatioAnswer,
            resultFev1FvcRatioAnswer);
    }, [calculateValuesForRow, predictedFev1FvcRatioAnswer, resultFev1FvcRatioAnswer]);

    React.useEffect(() => calculateFvcValues(), [calculateFvcValues]);
    React.useEffect(() => calculateFev1Values(), [calculateFev1Values]);
    React.useEffect(() => calculateFev1FvcRatioValues(), [calculateFev1FvcRatioValues]);

    const inPastValidator = useTimeHasAlreadyPassedValidator();
    const timeIsAfterValidator = useTimeIsAfterValidator(Jan1st1900);

    const dateValidators = React.useMemo(() => [inPastValidator, timeIsAfterValidator], [inPastValidator, timeIsAfterValidator]);

    const ageInitialAnswer = React.useMemo(() => employeeDob && moment().diff(employeeDob, "years"), [employeeDob]);

    return (
        <ContextAwareProgressBarQuestionnaireSection
            {...props}
            {...sections.Spirometry}
        >
            {YesNoWithFollowUpQuestion({
                readonly,
                questionConfigs: [
                    getQuestionConfig(questionConfigs, questionIds.SpirometryUnableToCarryOut),
                    getQuestionConfig(questionConfigs, questionIds.SpirometryUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: true,
                questions: propsQuestions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.SpirometryUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <TextQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryMachineSerialNumber)}
                    mandatory
                />
                <DateQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryMachineCalibrationDate)}
                    mandatory
                    validators={dateValidators}
                />
                <DropdownQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryReasonForAttending)}
                    options={ReasonsForAttending.map(g => ({ text: g, value: g }))}
                    mandatory
                />
                <DateQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryLastTestDate)}
                    mandatory
                    validators={dateValidators}
                />
                <DropdownQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryGender)}
                    options={Genders.map(g => ({ text: g, value: g }))}
                    mandatory
                />
                <NumberQuestion
                    readonly
                    answer={ageInitialAnswer}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryAge)}
                />
                {/* I made an attempt to convert the SpirometryTestStartDate question to DateQuestion with initialAnswer but could not get it to work
                    as a readonly field. The field was always marked as invalid and was blocking form submission. */}
                <OldDateQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SpirometryTestStartDate)}
                    answer={moment().format("YYYY-MM-DD")}
                    mandatory
                />
                <TextQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryCurrentOrHistoricalIssues)}
                    mandatory
                />
                <p>Height (cm)</p>
                <Form.Field>{getAnswerToQuestionInSectionWithId(sections.HealthQuestions.id, questionIds.Height)}</Form.Field>
                <p>Weight (kg)</p>
                <Form.Field>{getAnswerToQuestionInSectionWithId(sections.HealthQuestions.id, questionIds.Weight)}</Form.Field>
                <DropdownQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryEthnicity)}
                    options={Ethnicities.map(e => ({ text: e, value: e }))}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometrySmoker)}
                    mandatory
                />
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell content="Test Type" />
                            <Table.HeaderCell content="Result" />
                            <Table.HeaderCell content="Predicted" />
                            <Table.HeaderCell content="% Predicted" />
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(questionConfigs, questionIds.SpirometryVcLitres) as CustomQuestionConfig).questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryVcLitres)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(questionConfigs, questionIds.SpirometryFvcLitres) as CustomQuestionConfig).questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFvcLitres)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFvcLitresPredicted)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFvcLitresPercentPredicted)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(questionConfigs, questionIds.SpirometryFev1Litres) as CustomQuestionConfig).questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFev1Litres)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFev1LitresPredicted)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFev1LitresPercentPredicted)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={
                                    (getQuestionConfig(questionConfigs, questionIds.SpirometryFev1FvcRatio) as CustomQuestionConfig).questionText
                                }
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFev1FvcRatio)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFev1FvcRatioPredicted)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFev1FvcRatioPercentPredicted)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(questionConfigs, questionIds.SpirometryFef2575) as CustomQuestionConfig).questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryFef2575)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(questionConfigs, questionIds.SpirometryPefrLitresPerMinute) as CustomQuestionConfig).questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryPefrLitresPerMinute)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
                <CheckboxQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryResultsExplained)}
                    value="Yes"
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryRequiresRegularFaceFitTesting)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryUnderstandsImportanceOfBeingCleanShaven)}
                    mandatory
                />
                <CheckboxQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryReferredDueToCleanShavenUnderstanding)}
                    value="Yes"
                />
                <MultiButtonQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryPpeAvailableForInspection)}
                    mandatory
                />
                {getAnswerToQuestionWithId(questions, questionIds.SpirometryPpeAvailableForInspection) === MultiButtonQuestion.YesOption &&
                    <>
                        <MultiButtonQuestion
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryPpeComfortableAndFitForPurpose)}
                            hideDivider
                            mandatory
                        />
                        <TextQuestion
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryPpeComfortableAndFitForPurposeComments)}
                        />
                    </>
                }
                <MultiButtonQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryRespiratoryProtectionLeafletOrAdviceGiven)}
                    mandatory
                />
                <DropdownQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryReferralRequired)}
                    options={[
                        { text: "No", value: "No" },
                        { text: "Referred to GP", value: "Referred to GP" },
                        { text: "Referral to company OHA/OHP", value: "Referral to company OHA/OHP" },
                        { text: "GP and company referral", value: "GP and company referral" }
                    ]}
                    mandatory
                />
                <DropdownQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryAdviceRecommendationForGpSupplied)}
                    options={[
                        { text: "Yes", value: "Yes" },
                        { text: "No", value: "No" },
                        { text: "Not required", value: "Not required" }
                    ]}
                    mandatory
                />
                <DropdownQuestion
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryReviewDateRecommended)}
                    options={[
                        { text: "12 weeks", value: "12 weeks" },
                        { text: "6 months", value: "6 months" },
                        { text: "Anually", value: "Anually" },
                        { text: reviewDateOtherAnswer, value: reviewDateOtherAnswer },
                    ]}
                    mandatory
                />
                {getAnswerToQuestionWithId(questions, questionIds.SpirometryReviewDateOtherDetails) === reviewDateOtherAnswer &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionConfigs, questionIds.SpirometryReviewDateOtherDetails)}
                        mandatory
                    />
                }
            </>}
        </ContextAwareProgressBarQuestionnaireSection>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: any) => {
    return {
        ...makeMapStateToQuestionnaireSectionProps(sections.Spirometry.id)(state, ownProps)
    };
};

export const HealthSurveillanceConsultationSpirometrySection = connect(
    mapStateToProps,
    mapDispatchToQuestionnaireSectionProps
)(HealthSurveillanceConsultationSpirometrySectionUnconnected);
